import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'What makes a good apology message?', a: 'A sincere apology acknowledges the specific harm, takes responsibility without excuses, expresses genuine remorse, and states what you\'ll do differently. Avoid phrases like "I\'m sorry if you felt..." which shift blame.' },
  { q: 'Is it okay to apologize in writing instead of in person?', a: 'Written apologies can be more thoughtful because you can choose your words carefully. They also give the recipient space to process. For deep hurts, a written message can be a powerful first step.' },
  { q: 'How do I apologize when I don\'t know what I did wrong?', a: 'Start by expressing that you can see the other person is hurt, and that their feelings matter to you. Ask for the chance to understand, and mean it.' },
  { q: 'Can Say It NOW help me write an apology?', a: 'Yes. Our AI companion JacKey is trained to help with sensitive messages, including apologies. It guides you through what to acknowledge, how to express remorse, and how to offer a path forward.' },
];

const related = [
  { label: 'Message to Someone You Love', href: '/message-to-someone-you-love' },
  { label: 'Heartfelt Message to Mom', href: '/heartfelt-message-to-mom' },
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
  { label: 'Apology Message Generator', href: '/tools/apology-message-generator' },
];

export default function ApologyMessageExamples() {
  return (
    <SEOPageLayout
      title="Apology Message Examples — Say Sorry Sincerely"
      metaDescription="Find the right words to apologize sincerely. Real apology message examples for family, friends, and partners. Write a heartfelt sorry message — free and private."
      h1="Apology Message Examples — How to Say Sorry and Mean It"
      subtitle="Saying sorry is hard. Here are real examples and guidance to help you find the right words."
      faq={faq}
      relatedPages={related}
    >
      <h2>Why Apologizing Is So Hard</h2>
      <p>
        Apologizing requires us to do something our ego fights against: admit we were wrong. It means sitting with the discomfort of knowing we hurt someone we care about. But here's the thing — <strong>a sincere apology is one of the most courageous things a person can do.</strong> It doesn't make you weak. It makes you someone worth trusting.
      </p>

      <h2>The Anatomy of a Real Apology</h2>
      <p>A meaningful apology has four parts:</p>
      <ol>
        <li><strong>Acknowledge specifically what you did.</strong> Not "I'm sorry for everything" — name the action.</li>
        <li><strong>Own it without excuses.</strong> No "but" after "I'm sorry."</li>
        <li><strong>Express empathy.</strong> Show you understand how it affected them.</li>
        <li><strong>Commit to change.</strong> Words without changed behavior are just noise.</li>
      </ol>

      <h2>Apology Message Examples</h2>

      <h3>To a Partner</h3>
      <blockquote>
        "I was wrong, and I know my words hurt you. You didn't deserve that. I let my frustration speak for me instead of treating you with the respect you always give me. I'm not just sorry — I'm committed to doing better, because you deserve better."
      </blockquote>

      <h3>To a Parent</h3>
      <blockquote>
        "Mom/Dad, I've been carrying guilt about the way I spoke to you. You didn't deserve those words. You've given me so much, and I repaid it with anger. I'm sorry — truly. I want to repair this, not just move past it."
      </blockquote>

      <h3>To a Friend</h3>
      <blockquote>
        "I owe you an apology. When you needed me, I wasn't there — and I didn't even explain why. That's not the kind of friend I want to be. You mean too much to me to let my silence speak louder than my care for you."
      </blockquote>

      <h3>To a Sibling</h3>
      <blockquote>
        "We've let too much go unsaid between us. I know I've hurt you in ways I didn't acknowledge at the time. I'm writing this because I want you to know — I see it now, and I'm sorry. Our relationship matters more to me than my pride."
      </blockquote>

      <h2>When Written Apologies Are Better</h2>
      <p>
        Some conversations are too emotionally charged for real-time dialogue. A written apology gives you the space to choose every word carefully and gives the other person the space to process without pressure. <Link to="/signup">Say It NOW</Link> was built for exactly these moments — messages that are too important to get wrong.
      </p>
      <p>
        You can also use our free <Link to="/tools/apology-message-generator">Apology Message Generator</Link> to get started if you're stuck on what to say.
      </p>

      <h2>Moving from Sorry to Healing</h2>
      <p>
        An apology is a beginning, not an ending. The message opens the door — but it's your actions that walk through it. Say It NOW's <Link to="/faq">conversation threads</Link> let the other person respond when they're ready, creating a safe space for dialogue and reconciliation.
      </p>
    </SEOPageLayout>
  );
}
